import { BaseAnalyticsProvider } from '../../BaseAnalyticsProvider'
import { MetaEventMethod } from './Meta.types'
import { ActionName, AnalyticsProvider, AnalyticsDevice } from '../../AnalyticsManager.types'
import { applyTransformations } from '../../actionDataTransformations'

import type { ActionDataKey, ProviderConfiguration, EventConfiguration } from '../../AnalyticsManager.types'
import type { MetaEventData, MetaDependency } from './Meta.types'

export default class Meta extends BaseAnalyticsProvider {
	private configuration: ProviderConfiguration

	private dependencies: MetaDependency

	private enabled = false

	constructor(configuration: ProviderConfiguration, dependencies: MetaDependency) {
		super()

		this.dependencies = dependencies
		this.enabled =
			(!!configuration.origin?.includes(AnalyticsDevice.MOBILE_APPLICATION) && this.dependencies.isMobileApp()) ||
			(!!configuration.origin?.includes(AnalyticsDevice.WEB_BROWSER) && !this.dependencies.isMobileApp())
		this.configuration = configuration
	}

	private static buildEventData(
		actionName: ActionName,
		eventName: string,
		actionData: Record<ActionDataKey, any>,
		eventProperties: EventConfiguration
	): MetaEventData {
		const lastArgument: Record<string, any> = {}
		const _arguments: unknown[] = []
		const isPurchaseEvent = actionName === ActionName.PURCHASE

		if (isPurchaseEvent && eventProperties.length < 2) {
			throw new Error('Meta requires at least two event properties for purchase events')
		}

		eventProperties.forEach((eventAttributeData, index) => {
			const eventProperty = eventAttributeData.attributeName
			const transformations = eventAttributeData?.transformations
			const actionDataKey = eventAttributeData?.dataKey
			const eventAttributeValue = applyTransformations(
				transformations,
				Object.prototype.hasOwnProperty.call(actionData, actionDataKey) ? actionData[actionDataKey as ActionDataKey] : actionDataKey
			)

			if (!actionDataKey) {
				return
			}

			if (isPurchaseEvent && index <= 1) {
				_arguments.push(eventAttributeValue)
				return
			}

			if (!isPurchaseEvent && index === 0) {
				_arguments.push(eventAttributeValue)
				return
			}

			lastArgument[eventProperty] = eventAttributeValue
		})

		_arguments.push(lastArgument)

		return {
			eventName,
			method: isPurchaseEvent ? MetaEventMethod.LOG_PURCHASE : MetaEventMethod.LOG_EVENT,
			arguments: _arguments as
				| [string | number | null, Record<string, unknown>]
				| [string | number, string | number, Record<string, unknown>]
				| [Record<string, unknown>],
		}
	}

	public async sendEvent(actionName: ActionName, actionData: Record<ActionDataKey, any>): Promise<void> {
		if (!this.enabled) {
			return
		}

		const actionConfiguration = this.configuration?.actions[actionName]
		if (!actionConfiguration) {
			return
		}

		for (const eventName of Object.keys(actionConfiguration)) {
			try {
				this.dependencies.sendEvent(
					AnalyticsProvider.META,
					Meta.buildEventData(actionName, eventName, actionData, actionConfiguration[eventName])
				)
			} catch (error) {
				console.error(error)
			}
		}
	}

	public async identifyUser(userId: string) {
		// Not implemented
	}

	public async resetUser() {
		// Not implemented
	}
}
